import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

//COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";

//REDUX/UTILS
import { KeyValue, KeyValueGeneric } from "@/main/models/common";
import { IntegrationCodeTypeEnum } from "@/main/services/enum/IntegrationCodeTypeEnum";
import {
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
	removeObjectsEmpty,
} from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getMappingType, useMappingTypeReducer } from "@/main/store/mappingType";
import { getIntegrationInterface, useIntegrationTypeReducer } from "@/main/store/integrationType";
import { IntegrationCodeSaveRequest } from "@/main/models/request";
import { integrationCodeApi } from "@/main/api";
import { IntegrationCodeEditedResponse, IntegrationCodeResponse } from "@/main/models/response";
import { SpinnerLoading } from "@/main/components/shared/styles";
import { getOptionsStatusActiveGeneral } from "@/main/services/data/data";
import { StatusActiveGeneralEnum } from "@/main/services/enum/StatusActiveGeneralEnum";

// STYLES/COMPONENTS LOCAL
import DinamicFiltersMappingCodes, {
	MERGED_INITIAL_STATE,
} from "../dinamicFiltersMappingCodes/dinamicFiltersMappingCodes";
import * as S from "./styles";
import { AssessmentTypeEnum } from "@/main/services/enum/AssessmentTypeEnum";
import { useReasonReducer } from "@/main/store/reason";

const INITIAL_FORM_STATE_DEFAULT = {
	country: null as KeyValue | null,
	mappingType: null as KeyValueGeneric<IntegrationCodeTypeEnum, string> | null,
	interface: null as KeyValue | null,
	status: null as KeyValue | null,
};
const INITIAL_FORM_STATE = {
	...INITIAL_FORM_STATE_DEFAULT,
	...MERGED_INITIAL_STATE,
};

type IFormFiltersMappingCodes = typeof INITIAL_FORM_STATE;

interface IModalMapCodeProps {
	isOpenModal: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	guidIntegrationCode?: string;
	updateRowDataGrid?: (row: IntegrationCodeResponse) => void;
	refreshSearchGrid?: (idMappingTypeModal: string) => void;
	closeModal: () => void;
}
function ModalMapCode({
	isOpenModal,
	guidIntegrationCode,
	updateRowDataGrid = () => {},
	refreshSearchGrid = () => {},
	closeModal,
}: IModalMapCodeProps) {
	const isEdit = !isNullUndefined(guidIntegrationCode);
	// #region Hooks
	const dispatch = useDispatch();
	const methods = useForm<IFormFiltersMappingCodes>({
		defaultValues: INITIAL_FORM_STATE,
	});
	const { control, handleSubmit, getValues, setValue } = methods;
	// #endregion

	// #region reducers
	const { countriesUser: countryReducer } = useCountryReducer();
	const { mappingType: mappingTypeReducer } = useMappingTypeReducer();
	const { integrationInterface: integrationInterfaceReducer } = useIntegrationTypeReducer();
	useReasonReducer();
	const optionsStatus = getOptionsStatusActiveGeneral();
	// #endregion

	// #region States
	const optionDefaultCountry = isEdit ? null : getDefaultOptionDrop(countryReducer.data);
	const optionDefaultStatus = isEdit
		? null
		: optionsStatus.find((item) => item.key === StatusActiveGeneralEnum.Active)!;
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(isEdit);
	// #endregion

	function loadEditIntegrationCode() {
		setIsLoadingEdit(true);
		// Notation: this reset of react-hook-form set the default values of the form
		integrationCodeApi
			.getIntegrationCodeByGuid(guidIntegrationCode as string)
			.then((response) => {
				const { result } = response.data;
				const formattedResult: IntegrationCodeEditedResponse = removeObjectsEmpty(result, true);

				const formattedStatus = formattedResult.active
					? optionsStatus.find((item) => item.key === StatusActiveGeneralEnum.Active)
					: optionsStatus.find((item) => item.key == StatusActiveGeneralEnum.Inactive);

				const backendForFrontend: IFormFiltersMappingCodes = {
					...INITIAL_FORM_STATE_DEFAULT,
					assessmentType: formattedResult.assessmentType,
					bodyshop: formattedResult.bodyshop,
					category: formattedResult.category,
					code: formattedResult.code,
					company: formattedResult.company,
					companyType: formattedResult.companyType,
					country: formattedResult.country,
					insurer: formattedResult.insurer,
					interface: formattedResult.integrationInterface,
					manufacturer: formattedResult.make,
					mappingType: formattedResult.integrationCodeType,
					model: formattedResult.model,
					part: formattedResult.part,
					position: formattedResult.position,
					subCategory: formattedResult.subCategory,
					subModel: formattedResult.trim,
					year: formattedResult.year,
					partName: formattedResult.part?.value,
					status: formattedStatus as KeyValue,
					supplier: formattedResult.supplier,
					supervisor: formattedResult.supervisor,
					valuator: formattedResult.valuator,
					address: formattedResult.address,
					insuranceSubline: formattedResult.insuranceSubline,
					claimFileCategory: formattedResult.claimFileCategory,
					reasonTotalLoss: formattedResult.reasonTotalLoss,
				};

				methods.reset(backendForFrontend);
			})
			.finally(() => {
				setIsLoadingEdit(false);
			});
	}
	// #region Core Functions
	function resetForm() {
		methods.reset();
		if (optionDefaultCountry) setValue("country", optionDefaultCountry);
		if (optionDefaultStatus) setValue("status", optionDefaultStatus);
	}
	function getDescriptionRowData(formValue: IFormFiltersMappingCodes) {
		const renderVehicle = `${formValue.manufacturer?.value} ${formValue.model?.value} ${formValue.subModel?.value} ${formValue.year?.value}`;
		const renderDescriptionRow = {
			[IntegrationCodeTypeEnum.AssessmentType]: formValue.assessmentType?.value,
			[IntegrationCodeTypeEnum.Bodyshop]: formValue.bodyshop?.value,
			[IntegrationCodeTypeEnum.Insurer]: formValue.insurer?.value,
			[IntegrationCodeTypeEnum.Supplier]: formValue.supplier?.value,
			[IntegrationCodeTypeEnum.Valuator]: formValue.valuator?.value,
			[IntegrationCodeTypeEnum.Supervisor]: formValue.supervisor?.value,
			[IntegrationCodeTypeEnum.Make]: formValue.manufacturer?.value,
			[IntegrationCodeTypeEnum.Model]: formValue.model?.value,
			[IntegrationCodeTypeEnum.Part]: formValue.part?.value,
			[IntegrationCodeTypeEnum.Submodel]: formValue.subModel?.value,
			[IntegrationCodeTypeEnum.Vehicle]: renderVehicle,
			[IntegrationCodeTypeEnum.SupplierByBuyer]: formValue.supplier?.value,
			[IntegrationCodeTypeEnum.Address]: formValue.address?.value,
			[IntegrationCodeTypeEnum.InsuranceSubline]: formValue.insuranceSubline?.value,
			[IntegrationCodeTypeEnum.ClaimFileCategory]: formValue.claimFileCategory?.value,
		};
		return renderDescriptionRow[formValue.mappingType?.key as IntegrationCodeTypeEnum] ?? "";
	}
	function resetDinamicFields() {
		const values = getValues();
		const keysFormDefault = Object.keys(INITIAL_FORM_STATE_DEFAULT);

		Object.keys(values).forEach((key) => {
			const isFormDefault = keysFormDefault.includes(key);
			if (isFormDefault) return;
			methods.resetField(key as keyof IFormFiltersMappingCodes);
		});
	}
	// #endregion

	// #region Async functions
	async function onSubmitSave(data: IFormFiltersMappingCodes) {
		setIsLoadingSave(true);
		const request: IntegrationCodeSaveRequest = {
			...(isEdit && { guidIntegrationCode }),
			guidCountry: String(data.country?.key),
			guidInterface: String(data.interface?.key),
			integrationCodeTypeEnum: data.mappingType?.key as IntegrationCodeTypeEnum,
			code: data.code,
			active: isNullUndefined(data.status) ? null : data.status?.key == StatusActiveGeneralEnum.Active,
			// Bodyshop = '1'
			guidBodyshop: String(data.bodyshop?.key),
			// Vehicle = '3' | Make = '7 | Model = '8' | SubModel = '9'
			guidMake: String(data.manufacturer?.key),
			guidModel: String(data.model?.key),
			guidSubModel: String(data.subModel?.key),
			guidYear: String(data.year?.key),
			// Part = '4'
			guidPartCategory: String(data.category?.key),
			guidPartSubCategory: String(data.subCategory?.key),
			guidPartPosition: String(data.position?.key),
			guidPart: String(data.part?.key),
			// Insurer = '5'
			guidInsurer: String(data.insurer?.key),
			// Assessment Type = '6'
			idAssessmentType: data.assessmentType?.key as AssessmentTypeEnum,
			guidCompany: String(data.company?.key),
			guidSupplier: String(data.supplier?.key),
			guidAddress: String(data.address?.key),
			guidInsuranceSubline: String(data.insuranceSubline?.key),
			idClaimFileCategory: data.claimFileCategory?.key ? Number(data.claimFileCategory?.key) : undefined,
			guidReasonTotalLoss: String(data.reasonTotalLoss?.key),
			guidValuator: String(data.valuator?.key),
			guidSupervisor: String(data.supervisor?.key),
		};

		integrationCodeApi
			.saveIntegrationCode(removeEmptyValues(request))
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				if (!isEdit) {
					refreshSearchGrid(String(data.mappingType?.key));
					resetForm();
				}
				if (isEdit) {
					methods.reset(data);
					updateRowDataGrid({
						code: data.code,
						description: getDescriptionRowData(data),
						companyName: data.company?.value as string,
						interfaceName: data.interface?.value as string,
						idIntegrationCodeType: data.mappingType?.key as IntegrationCodeTypeEnum,
						integrationCodeTypeName: data.mappingType?.value as string,
						active: data.status?.key == StatusActiveGeneralEnum.Active,
						guidIntegrationCode: guidIntegrationCode,
					});
				}
				closeModal();
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}
	// #endregion

	// #region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		if (isNullUndefined(mappingTypeReducer.data)) dispatch(getMappingType());
		if (isNullUndefined(integrationInterfaceReducer.data)) dispatch(getIntegrationInterface());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(guidIntegrationCode)) {
			loadEditIntegrationCode();
		}
	}, [guidIntegrationCode]);
	useEffect(() => {
		if (isEdit && !isOpenModal) resetForm();
	}, [isOpenModal]);
	// #endregion

	//#region Disabled
	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
	const disabledMappingType = isNullUndefined(mappingTypeReducer.data) || mappingTypeReducer?.data?.length === 1;
	const disabledIntegrationInterface =
		isNullUndefined(integrationInterfaceReducer.data) || integrationInterfaceReducer?.data?.length === 1;
	// #endregion

	if (isLoadingEdit) return <SpinnerLoading />;

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						id="id-mappingCodes-modal-select-country"
						nameCustom="country"
						label={getTranslateByKey("information.country")}
						isDefaultValueUnique
						valueDefaultKey={optionDefaultCountry?.key}
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						disabled={isEdit || disabledCountry}
						onChangeCustom={() => {}}
					/>
					<KogAutoCompleteControlled
						id="id-mappingCodes-modal-select-interface"
						nameCustom="interface"
						label={getTranslateByKey("information.interface")}
						isDefaultValueUnique
						control={control}
						setValueCustom={setValue}
						options={integrationInterfaceReducer.data}
						isLoading={integrationInterfaceReducer.isLoading}
						disabled={disabledIntegrationInterface}
						onChangeCustom={() => {}}
					/>
					<KogAutoCompleteControlled
						id="id-mappingCodes-modal-select-mappingType"
						nameCustom="mappingType"
						label={getTranslateByKey("information.mappingType")}
						isDefaultValueUnique
						control={control}
						setValueCustom={setValue}
						options={mappingTypeReducer.data}
						isLoading={mappingTypeReducer.isLoading}
						rulesCustom={{ required: true }}
						disabled={isEdit || disabledMappingType}
						onChangeCustom={() => {
							resetDinamicFields();
						}}
					/>
					<DinamicFiltersMappingCodes
						keyReducer={isEdit ? `id-mappingCodes-modalMapCodeEdit` : "id-mappingCodes-modalMapCode"}
						isEdit={isEdit}
						INITIAL_FORM_STATE_DEFAULT={INITIAL_FORM_STATE_DEFAULT}
						fieldsRequired={[
							"assessmentType",
							"bodyshop",
							"insurer",
							"manufacturer",
							"model",
							"subModel",
							"category",
							"subCategory",
							"position",
							"part",
							"year",
							"code",
							"companyType",
							"company",
							"address",
							"supplier",
							"supervisor",
							"valuator",
							"insuranceSubline",
							"claimFileCategory",
							"reasonTotalLoss",
						]}
					/>
					<KogAutoCompleteControlled
						id="id-mappingCodes-modal-select-status"
						nameCustom="status"
						label={getTranslateByKey("information.status")}
						valueDefaultKey={optionDefaultStatus?.key}
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={optionsStatus}
						onChangeCustom={() => {}}
					/>
					<S.AreaButtons>
						<SecondaryButton id="id-mappingCodes-modal-btn-save" type="submit" isLoading={isLoadingSave}>
							{getTranslateByKey("information.save")}
						</SecondaryButton>
					</S.AreaButtons>
				</S.Container>
			</form>
		</FormProvider>
	);
}

function WrapperModalMapCode(props: IModalMapCodeProps) {
	const isEdit = !isNullUndefined(props.guidIntegrationCode);
	const titleSaveModal = getTranslateByKey("information.newMapCode");
	const titleEditModal = `${getTranslateByKey("information.edit")} ${getTranslateByKey("information.mapCode").toLowerCase()}`;
	const titleModal = isEdit ? titleEditModal : titleSaveModal;

	return (
		<KogModalSideComponent
			open={props.isOpenModal}
			shouldRenderOpen
			title={titleModal}
			closeModal={() => props.setIsOpenModal(false)}
		>
			<ModalMapCode {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalMapCode;
